import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faMagnifyingGlass,
  faChevronDown,
  faChevronUp,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import { useStores } from '../../components/Stores/hooks';
import { setActiveStore } from '../../components/Stores/store/userStoresSlice';
import type { Store } from '../../components/Stores/store/userStoresSlice';
import { OpenStatus } from './types';
import {
  Container,
  TriggerCard,
  StoreAvatar,
  TriggerBody,
  TriggerName,
  TriggerMeta,
  DropdownContainer,
  SearchInput,
  OptionList,
  OptionItem,
  OptionMain,
  OptionTextGroup,
  OptionName,
  OptionAddress,
  OptionSide,
  StatusBadge,
  EmptyState,
  LoadingContainer,
  Divider,
  AddStoreRow,
  AddStoreIcon,
  AddStoreTitle,
  AddStoreSubtitle,
} from './styles';

// operationalStatus values aren't standardized across the API yet, so we
// only render a status indicator when we can confidently tell open from closed.
const getOpenStatus = (operationalStatus?: string): OpenStatus => {
  if (!operationalStatus) return null;
  return operationalStatus.toLowerCase().includes('open');
};

// The store's photo when it has one; otherwise the faStore icon acts as the
// empty-state placeholder — never render neither.
const StoreAvatarContent: React.FC<{ store?: Store }> = ({ store }) =>
  store?.imageUrl ? (
    <img src={store.imageUrl} alt={store.name} />
  ) : (
    <FontAwesomeIcon icon={faStore} />
  );

const StoreSwitcher: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' };

  const { data: stores = [], isLoading: storesLoading } = useStores();
  const { activeStoreId } = useSelector((state: RootState) => state.stores);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownWidth, setDropdownWidth] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (stores.length === 0) return;
    const activeStoreExists = stores.some((store) => store.id === activeStoreId);
    if (!activeStoreExists) {
      dispatch(setActiveStore(stores[0].id));
    }
  }, [stores, activeStoreId, dispatch]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const updateWidth = () => setDropdownWidth(el.offsetWidth);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const activeStore = stores.find((store) => store.id === activeStoreId);
  const activeStoreStatus = getOpenStatus(activeStore?.operationalStatus);

  const filteredStores = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return stores;
    return stores.filter((store) => {
      const haystack = `${store.name || ''} ${store.address || ''} ${store.city || ''}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [stores, searchQuery]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  };

  const handleSelect = (store: Store) => {
    dispatch(setActiveStore(store.id));
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleAddStore = () => {
    setIsOpen(false);
    navigate('/stores');
  };

  return (
    <Container theme={theme} ref={containerRef}>
      <Dropdown
        trigger={['click']}
        open={isOpen}
        onOpenChange={handleOpenChange}
        disabled={storesLoading || stores.length === 0}
        dropdownRender={() => (
          <DropdownContainer
            theme={theme}
            style={dropdownWidth ? { width: Math.max(dropdownWidth, 340) } : undefined}
          >
            {storesLoading ? (
              <LoadingContainer>
                <Spin size="small" />
              </LoadingContainer>
            ) : (
              <>
                <SearchInput
                  placeholder="Search stores by name"
                  prefix={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  allowClear
                />
                <OptionList>
                  {filteredStores.length === 0 && (
                    <EmptyState theme={theme}>
                      No stores match "{searchQuery}"
                    </EmptyState>
                  )}
                  {filteredStores.map((store) => {
                    const openStatus = getOpenStatus(store.operationalStatus);
                    const isSelected = store.id === activeStoreId;
                    return (
                      <OptionItem
                        key={store.id}
                        type="button"
                        isSelected={isSelected}
                        theme={theme}
                        onClick={() => handleSelect(store)}
                      >
                        <OptionMain>
                          <StoreAvatar theme={theme} status={openStatus}>
                            <StoreAvatarContent store={store} />
                          </StoreAvatar>
                          <OptionTextGroup>
                            <OptionName theme={theme}>{store.name}</OptionName>
                            {(store.address || store.city) && (
                              <OptionAddress theme={theme}>
                                {store.address || store.city}
                              </OptionAddress>
                            )}
                          </OptionTextGroup>
                        </OptionMain>
                        <OptionSide>
                          {openStatus !== null && (
                            <StatusBadge status={openStatus}>{openStatus ? 'Open' : 'Closed'}</StatusBadge>
                          )}
                        </OptionSide>
                      </OptionItem>
                    );
                  })}
                </OptionList>

                <Divider theme={theme} />
                <AddStoreRow type="button" theme={theme} onClick={handleAddStore}>
                  <AddStoreIcon theme={theme}>
                    <FontAwesomeIcon icon={faPlus} />
                  </AddStoreIcon>
                  <OptionTextGroup>
                    <AddStoreTitle>Add New Store</AddStoreTitle>
                    <AddStoreSubtitle theme={theme}>Create and manage a new store location</AddStoreSubtitle>
                  </OptionTextGroup>
                </AddStoreRow>
              </>
            )}
          </DropdownContainer>
        )}
      >
        <TriggerCard theme={theme} type="button" onClick={(e) => e.preventDefault()}>
          <StoreAvatar theme={theme} status={activeStoreStatus}>
            <StoreAvatarContent store={activeStore} />
          </StoreAvatar>
          <TriggerBody>
            <TriggerName theme={theme}>
              {activeStore ? activeStore.name : stores.length === 0 ? 'No stores found' : 'Select a store'}
            </TriggerName>
            {activeStore && (activeStore.address || activeStore.city) && (
              <TriggerMeta theme={theme}>{activeStore.address || activeStore.city}</TriggerMeta>
            )}
          </TriggerBody>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </TriggerCard>
      </Dropdown>
    </Container>
  );
};

export default StoreSwitcher;
